import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Component, ReactNode, ErrorInfo, lazy, Suspense } from 'react';
import { reportarErro } from './lib/reportarErro';
import { getSessao } from './lib/auth';
import { temAcessoPagina } from './lib/permissoes';

function CarregandoTela() {
  return <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>Carregando…</div>;
}

// Depois de um deploy, quem está com o Portal aberto pode pedir o pedaço de uma tela que mudou de nome:
// recarrega a página uma vez (no máximo a cada 30s) em vez de mostrar erro.
const ERRO_PEDACO = /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Loading chunk/i;
function recarregarSePedacoAntigo(error: Error): boolean {
  if (!ERRO_PEDACO.test(error?.message ?? '')) return false;
  try {
    const ultima = Number(sessionStorage.getItem('recarregou_pedaco') || 0);
    if (Date.now() - ultima < 30_000) return false;
    sessionStorage.setItem('recarregou_pedaco', String(Date.now()));
  } catch { /* sem sessionStorage: recarrega mesmo assim */ }
  window.location.reload();
  return true;
}

class ErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state = { error: null };
  static getDerivedStateFromError(error: Error) { return { error }; }
  componentDidCatch(error: Error, info: ErrorInfo) { if (recarregarSePedacoAntigo(error)) return; console.error('[ErrorBoundary]', error, info); reportarErro(error, { origem: 'tela' }); }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: 32, fontFamily: 'monospace' }}>
          <h2 style={{ color: '#dc2626' }}>Erro na interface</h2>
          <pre style={{ background: '#fef2f2', padding: 16, borderRadius: 8, overflow: 'auto', fontSize: 13 }}>
            {(this.state.error as Error).message}
            {'\n\n'}
            {(this.state.error as Error).stack}
          </pre>
          <button onClick={() => this.setState({ error: null })} style={{ marginTop: 16, padding: '8px 16px' }}>
            Tentar novamente
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
import { SupabaseProvider } from './lib/supabase';
import { AuthGuard } from './components/AuthGuard';
import { Shell } from './components/Shell/Shell';
import Avisos from './components/Avisos';
// Cada tela é carregada só quando é aberta (lazy): antes o Portal baixava as 59 telas num arquivo de ~2,9 MB no
// primeiro acesso. O Suspense abaixo mostra "Carregando…" enquanto o pedaço da tela chega.
const HomePage = lazy(() => import('./pages/Home').then((m) => ({ default: m.HomePage })));
const ComercialDashboardPage = lazy(() => import('./pages/ComercialDashboard').then((m) => ({ default: m.ComercialDashboardPage })));
const ComercialVendasPage = lazy(() => import('./pages/ComercialVendas').then((m) => ({ default: m.ComercialVendasPage })));
const ComercialRelatorioPage = lazy(() => import('./pages/ComercialRelatorio').then((m) => ({ default: m.ComercialRelatorioPage })));
const ComercialConfigPage = lazy(() => import('./pages/ComercialConfig').then((m) => ({ default: m.ComercialConfigPage })));
const GestaoAlunos = lazy(() => import('./pages/GestaoAlunos'));
const CorrigirEmail = lazy(() => import('./pages/CorrigirEmail'));
const AlunosAVencer = lazy(() => import('./pages/AlunosAVencer'));
const AlunosAtrasados = lazy(() => import('./pages/AlunosAtrasados'));
const AlunosSemEstudar = lazy(() => import('./pages/AlunosSemEstudar'));
const AlunosAbas = lazy(() => import('./pages/AlunosAbas'));
const Aprovados = lazy(() => import('./pages/Aprovados'));
const Processo = lazy(() => import('./pages/Processo'));
const Conversas = lazy(() => import('./pages/Conversas'));
const RespostasFormulario = lazy(() => import('./pages/RespostasFormulario'));
const FormularioVisualizar = lazy(() => import('./pages/FormularioVisualizar'));
const LeadsEsteira = lazy(() => import('./pages/LeadsEsteira'));
const ErrosReportados = lazy(() => import('./pages/ErrosReportados'));
const ProblemasPortal = lazy(() => import('./pages/ProblemasPortal'));
const BonusEntregas = lazy(() => import('./pages/BonusEntregas'));
const Embaixadores = lazy(() => import('./pages/Embaixadores'));
const Links = lazy(() => import('./pages/Links'));
const Negociacao = lazy(() => import('./pages/Negociacao'));
const ConteudoSocialSeller = lazy(() => import('./pages/ConteudoSocialSeller'));
const ConteudoDebriefing = lazy(() => import('./pages/ConteudoDebriefing'));
const ConteudoDirect = lazy(() => import('./pages/ConteudoDirect'));
const ConteudoPromptSocialSeller = lazy(() => import('./pages/ConteudoPromptSocialSeller'));
const MarketingEmail = lazy(() => import('./pages/MarketingEmail'));
const AtendimentoPainel = lazy(() => import('./pages/AtendimentoPainel'));
const AtendimentoComunidade = lazy(() => import('./pages/AtendimentoComunidade'));
const MarketingFunis = lazy(() => import('./pages/MarketingFunis'));
const Atividades = lazy(() => import('./pages/Atividades'));
const ConteudoCalendario = lazy(() => import('./pages/ConteudoCalendario'));
const ConteudoEspecialistas = lazy(() => import('./pages/ConteudoEspecialistas'));
const Assistente = lazy(() => import('./pages/Assistente'));
const MarketingTrafego = lazy(() => import('./pages/MarketingTrafego'));
const MarketingGrupos = lazy(() => import('./pages/MarketingGrupos'));
const Simulados = lazy(() => import('./pages/Simulados'));
const Encontros = lazy(() => import('./pages/Encontros'));
const CentralWhatsApp = lazy(() => import('./pages/CentralWhatsApp'));
const WhatsAppGerenciador = lazy(() => import('./pages/WhatsAppGerenciador'));
const WhatsAppConfig = lazy(() => import('./pages/WhatsAppConfig'));
const WhatsAppConhecimento = lazy(() => import('./pages/WhatsAppConhecimento'));
const WhatsAppSimulador = lazy(() => import('./pages/WhatsAppSimulador'));
const WhatsAppDisparos = lazy(() => import('./pages/WhatsAppDisparos'));
// [OCULTO jul/2026] Cronograma Lei Seca — feature desativada no portal de gestão.
const CadernosQuestoes = lazy(() => import('./pages/CadernosQuestoes'));
const ListaDiscursivas = lazy(() => import('./pages/ListaDiscursivas'));
const AdminUsuarios = lazy(() => import('./pages/AdminUsuarios'));
const AdminPerfis = lazy(() => import('./pages/AdminPerfis'));
const AdminLogs = lazy(() => import('./pages/AdminLogs'));
const AdminCorretores = lazy(() => import('./pages/AdminCorretores'));
const MigracaoTutory = lazy(() => import('./pages/MigracaoTutory'));
const CalendarioAcoes = lazy(() => import('./pages/CalendarioAcoes'));
const CalendarioLancamentos = lazy(() => import('./pages/CalendarioLancamentos'));
const AdminFlashcards = lazy(() => import('./pages/AdminFlashcards'));
// [OCULTO jul/2026] Hub Admin — feature desativada no portal de gestão.
const AdminMetas = lazy(() => import('./pages/AdminMetas'));
const AdminAlunosConfig = lazy(() => import('./pages/AdminAlunosConfig'));
const AdminAulasApoio = lazy(() => import('./pages/AdminAulasApoio'));
const AdminComeceAqui = lazy(() => import('./pages/AdminComeceAqui'));
const AdminDiscursivas = lazy(() => import('./pages/AdminDiscursivas'));
const AdminDiscursivasCorrecao = lazy(() => import('./pages/AdminDiscursivasCorrecao'));
const AdminCadernos = lazy(() => import('./pages/AdminCadernos'));
// [OCULTO jul/2026] Criador de Resumos — feature desativada no portal de gestão.
const AdminCronogramaPlanos = lazy(() => import('./pages/AdminCronogramaPlanos'));
const AdminConfigPlano = lazy(() => import('./pages/AdminConfigPlano'));
const AdminAvisosPlano = lazy(() => import('./pages/AdminAvisosPlano'));
const AdminSimuladosCronograma = lazy(() => import('./pages/AdminSimuladosCronograma'));
const Homologacao = lazy(() => import('./pages/Homologacao'));
const AvaliacoesConteudo = lazy(() => import('./pages/AvaliacoesConteudo'));
const BancoQuestoesPainel = lazy(() => import('./pages/BancoQuestoes/Painel'));
const BancoQuestoesLista = lazy(() => import('./pages/BancoQuestoes/Lista'));
const BancoQuestoesRevisao = lazy(() => import('./pages/BancoQuestoes/Revisao'));
const BancoQuestoesRecursos = lazy(() => import('./pages/BancoQuestoes/Recursos'));
const BancoQuestoesComentarios = lazy(() => import('./pages/BancoQuestoes/Comentarios'));
const BancoQuestoesAcessos = lazy(() => import('./pages/BancoQuestoes/Acessos'));
const BancoQuestoesIpQuestoes = lazy(() => import('./pages/BancoQuestoes/IpQuestoes'));
const BancoQuestoesPorMeta = lazy(() => import('./pages/BancoQuestoes/PorMeta'));
import { CENTRAL_ORDENS_PATHS, centralPath } from './pages/CentralOrdens/paths';
// Gestão da equipe (Tailwind próprio): carregada só quando alguém abre /ordens
const CentralOrdens = lazy(() => import('./pages/CentralOrdens'));

// Barreira de acesso por URL: bloqueia páginas fora das categorias do usuário.
// Mesmas regras do legado (lib/permissoes). Sem acesso → redireciona para Home.
function PageGuard({ children }: { children: ReactNode }) {
  const location = useLocation();
  const sessao = getSessao();
  const page = location.pathname.replace(/^\//, '') || 'home';
  // perfil admin (is_admin) conta como categoria admin
  const categorias = [...(sessao?.categorias ?? []), ...(sessao?.is_admin ? ['admin'] : [])];
  if (!temAcessoPagina(page, categorias)) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

// Lançamentos, Mensagens de grupo e Mensagens e API viraram Marketing › Funis (28/10/2026): links antigos (?id=…) abrem o funil
function ParaFunis() {
  const { search } = useLocation();
  return <Navigate to={`/marketing-funis${search}`} replace />;
}

export default function App() {
  return (
    <SupabaseProvider>
      <AuthGuard>
        <Shell>
          <ErrorBoundary>
          <PageGuard>
          <Suspense fallback={<CarregandoTela />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/comercial-dashboard" element={<ComercialDashboardPage />} />
            <Route path="/comercial-vendas" element={<ComercialVendasPage />} />
            <Route path="/comercial-relatorio" element={<ComercialRelatorioPage />} />
            <Route path="/comercial-config" element={<ComercialConfigPage />} />
            <Route path="/pessoas" element={<GestaoAlunos />} />
            <Route path="/corrigir-email" element={<CorrigirEmail />} />
            <Route path="/alunos-a-vencer" element={<AlunosAVencer />} />
            <Route path="/alunos-atrasados" element={<AlunosAtrasados />} />
            <Route path="/alunos-sem-estudar" element={<AlunosSemEstudar />} />
            <Route path="/alunos-gestao" element={<AlunosAbas />} />
            <Route path="/aprovados" element={<Aprovados />} />
            <Route path="/processo" element={<Processo />} />
            <Route path="/conversas" element={<Conversas />} />
            <Route path="/central-whatsapp" element={<CentralWhatsApp />} />
            <Route path="/whatsapp-gerenciador" element={<WhatsAppGerenciador />} />
            <Route path="/whatsapp-disparos" element={<WhatsAppDisparos />} />
            <Route path="/whatsapp-config" element={<WhatsAppConfig />} />
            <Route path="/whatsapp-conhecimento" element={<WhatsAppConhecimento />} />
            <Route path="/whatsapp-simulador" element={<WhatsAppSimulador />} />
            <Route path="/respostas-formulario" element={<RespostasFormulario />} />
            <Route path="/formulario-visualizar" element={<FormularioVisualizar />} />
            <Route path="/leads-esteira" element={<LeadsEsteira />} />
            <Route path="/erros-reportados" element={<ErrosReportados />} />
            <Route path="/problemas-portal" element={<ProblemasPortal />} />
            <Route path="/bonus" element={<BonusEntregas />} />
            <Route path="/embaixadores" element={<Embaixadores />} />
            <Route path="/conteudo-social-seller" element={<ConteudoSocialSeller />} />
            <Route path="/conteudo-debriefing" element={<ConteudoDebriefing />} />
            <Route path="/conteudo-direct" element={<ConteudoDirect />} />
            <Route path="/conteudo-prompt-social-seller" element={<ConteudoPromptSocialSeller />} />
            <Route path="/marketing-lancamentos" element={<ParaFunis />} />
            <Route path="/marketing-email" element={<MarketingEmail />} />
            <Route path="/marketing-mensagens" element={<Navigate to="/marketing-funis?aba=hoje" replace />} />
            <Route path="/marketing-grupos" element={<MarketingGrupos />} />
            <Route path="/atendimento-painel" element={<AtendimentoPainel />} />
            <Route path="/atendimento-comunidade" element={<AtendimentoComunidade />} />
            <Route path="/marketing-funis" element={<MarketingFunis />} />
            <Route path="/marketing-atividades" element={<Atividades />} />
            <Route path="/atividades" element={<Atividades />} />
            <Route path="/conteudo-calendario" element={<ConteudoCalendario />} />
            <Route path="/conteudo-especialistas" element={<ConteudoEspecialistas />} />
            <Route path="/assistente" element={<Assistente />} />
            <Route path="/marketing-trafego" element={<MarketingTrafego />} />
            <Route path="/negociacao" element={<Negociacao />} />
            <Route path="/links" element={<Links />} />
            <Route path="/avaliacoes-conteudo" element={<AvaliacoesConteudo />} />
            <Route path="/banco-questoes-painel" element={<BancoQuestoesPainel />} />
            <Route path="/banco-questoes-lista" element={<BancoQuestoesLista />} />
            <Route path="/banco-questoes-revisao" element={<BancoQuestoesRevisao />} />
            <Route path="/banco-questoes-recursos" element={<BancoQuestoesRecursos />} />
            <Route path="/banco-questoes-comentarios" element={<BancoQuestoesComentarios />} />
            <Route path="/banco-questoes-acessos" element={<BancoQuestoesAcessos />} />
            <Route path="/banco-questoes-ipq" element={<BancoQuestoesIpQuestoes />} />
            <Route path="/banco-questoes-por-meta" element={<BancoQuestoesPorMeta />} />
            <Route path="/admin-simulados" element={<Simulados />} />
            <Route path="/encontros" element={<Encontros />} />
            {/* endereços antigos do menu → /ordens/* */}
            {Object.keys(CENTRAL_ORDENS_PATHS).map((p) => <Route key={p} path={`/${p}`} element={<Navigate to={centralPath(p)!} replace />} />)}
            <Route path="/ordens/*" element={<Suspense fallback={null}><CentralOrdens /></Suspense>} />
            <Route path="/cadernos-questoes" element={<CadernosQuestoes />} />
            <Route path="/lista-discursivas" element={<ListaDiscursivas />} />
            <Route path="/admin-usuarios" element={<AdminUsuarios />} />
            <Route path="/admin-perfis" element={<AdminPerfis />} />
            <Route path="/admin-logs" element={<AdminLogs />} />
            <Route path="/admin-corretores" element={<AdminCorretores />} />
            <Route path="/migracao-tutory" element={<MigracaoTutory />} />
            <Route path="/calendario-acoes" element={<CalendarioAcoes />} />
            <Route path="/calendario-lancamentos" element={<CalendarioLancamentos />} />
            <Route path="/admin-flashcards" element={<AdminFlashcards />} />
            <Route path="/admin-metas" element={<AdminMetas />} />
            <Route path="/admin-alunos-config" element={<AdminAlunosConfig />} />
            <Route path="/admin-aulas-apoio" element={<AdminAulasApoio />} />
            <Route path="/admin-comece-aqui" element={<AdminComeceAqui />} />
            <Route path="/admin-discursivas" element={<AdminDiscursivas />} />
            <Route path="/admin-discursivas-correcao" element={<AdminDiscursivasCorrecao />} />
            <Route path="/admin-cadernos" element={<AdminCadernos />} />
            <Route path="/admin-cronograma-planos" element={<AdminCronogramaPlanos />} />
            <Route path="/admin-config-plano" element={<AdminConfigPlano />} />
            <Route path="/admin-avisos-plano" element={<AdminAvisosPlano />} />
            <Route path="/admin-simulados-cronograma" element={<AdminSimuladosCronograma />} />
            <Route path="/homologacao" element={<Homologacao />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </Suspense>
          </PageGuard>
          </ErrorBoundary>
        </Shell>
      </AuthGuard>
      <Avisos />
    </SupabaseProvider>
  );
}
